/**
 * Decisions: one register for every decision the company takes. A lever
 * proposed in Finance becomes one; so does a director decision, a written
 * resolution or a reserved matter. Each goes through delegated authority
 * (approvals.ts) and, when its type says so, a consent group's consent.
 *
 * Draft → Proposed → (Awaiting consent) → Taken, or Not taken. A proposal can
 * be withdrawn; a taken decision can be superseded by a later one or revoked,
 * until the date after which it can't be revisited.
 */
import { addDays, type ISODate } from "./dates";
import { approvalProgress, type ApprovalDecision, type ApprovalRequirement } from "./approvals";
import type { LeverEntry } from "./types";

export type DecisionStatus = "draft" | "proposed" | "awaiting_consent" | "taken" | "not_taken" | "withdrawn" | "superseded" | "revoked";
export const DECISION_STATUS_LABEL: Record<DecisionStatus, string> = {
  draft: "Draft",
  proposed: "Proposed",
  awaiting_consent: "Awaiting consent",
  taken: "Taken",
  not_taken: "Not taken",
  withdrawn: "Withdrawn",
  superseded: "Superseded",
  revoked: "Revoked",
};
/** Still being decided. */
export const DECISION_OPEN: DecisionStatus[] = ["draft", "proposed", "awaiting_consent"];

export interface DecisionType {
  id: string;
  name: string;
  /** "lever": the type every lever proposed in Finance takes. It can be renamed, never removed. */
  system: "lever" | null;
  /** Needs this group's consent once approved. */
  consentGroupId: string | null;
  active: boolean;
  sortOrder: number;
}

export type ConsentComparator = "over" | "at_least";
export const CONSENT_COMPARATOR_LABEL: Record<ConsentComparator, string> = { over: "Over", at_least: "At least" };

export interface ConsentParty {
  id: string;
  name: string;
  /** Shares or votes: what the threshold is a share of. */
  weight: number;
  active: boolean;
}

/** Investors, shareholders: whose consent some decisions need, and how much of it. */
export interface ConsentGroup {
  id: string;
  name: string;
  comparator: ConsentComparator;
  /** Percentage of the group's weight. */
  pct: number;
  active: boolean;
  sortOrder: number;
  parties: ConsentParty[];
}

/** Whose consent a decision needs, as the group stood when it was proposed. */
export interface ConsentRequirement {
  groupId: string;
  label: string;
  comparator: ConsentComparator;
  pct: number;
  parties: { id: string; name: string; weight: number }[];
}

export interface ConsentRecord {
  id: string;
  partyId: string;
  /** Consented, or refused. */
  given: boolean;
  date: ISODate;
  documentUrl: string | null;
  note: string | null;
  recordedBy: string | null;
  recordedAt: string;
}

export interface Decision {
  id: string;
  ref: number;
  typeId: string;
  title: string;
  /** The resolution itself: "THAT …". */
  wording: string;
  rationale: string;
  /** Pence: what the approval rules read. For a lever, worked out from its dates and amounts. */
  amount: number | null;
  connectedDirectorId: string | null;
  /** The lever it decides, for a lever's decision. */
  leverId: string | null;
  /** A lever's dates and amounts as proposed: what was approved. */
  entries: LeverEntry[] | null;
  status: DecisionStatus;
  /** Recorded as already taken (at a meeting, say), with who decided, rather than approved here. */
  recorded: boolean;
  /** Who must approve it, worked out when it was proposed. */
  approval: ApprovalRequirement | null;
  approvals: ApprovalDecision[];
  consent: ConsentRequirement | null;
  consents: ConsentRecord[];
  supersedesId: string | null;
  supersededById: string | null;
  reviewBy: ISODate | null;
  /** Can't be revisited after this date (a claim deadline, say): from then on it's final. */
  finalAfter: ISODate | null;
  lastReviewedAt: string | null;
  takenOn: ISODate | null;
  proposedBy: string | null;
  proposedAt: string | null;
  /** Who withdrew, revoked or decided against it, when, and why. */
  closedBy: string | null;
  closedAt: string | null;
  closedNote: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const decisionRef = (n: number) => `D-${String(n).padStart(3, "0")}`;
/** "Investors'", "Maggie's". */
export const possessive = (name: string) => (/s$/i.test(name) ? `${name}'` : `${name}'s`);

/* ------------------------------------------------------------------ */
/* Consent                                                             */
/* ------------------------------------------------------------------ */

export function consentMet(weight: number, total: number, comparator: ConsentComparator, pct: number): boolean {
  if (total <= 0) return false;
  const share = (weight / total) * 100;
  return comparator === "over" ? share > pct : share >= pct - 1e-9;
}

export interface ConsentProgress {
  /** Weight that has consented, and refused, of the total. */
  given: number;
  refused: number;
  total: number;
  met: boolean;
  /** Enough have refused that the threshold can't be met. */
  failed: boolean;
  /** Parties not heard from yet. */
  waiting: string[];
}

/** Each party's latest record counts. */
export function consentProgress(req: ConsentRequirement, records: ConsentRecord[]): ConsentProgress {
  const latest = new Map<string, ConsentRecord>();
  for (const r of [...records].sort((a, b) => a.recordedAt.localeCompare(b.recordedAt))) latest.set(r.partyId, r);
  const total = req.parties.reduce((s, p) => s + p.weight, 0);
  let given = 0;
  let refused = 0;
  const waiting: string[] = [];
  for (const p of req.parties) {
    const r = latest.get(p.id);
    if (!r) waiting.push(p.id);
    else if (r.given) given += p.weight;
    else refused += p.weight;
  }
  const met = consentMet(given, total, req.comparator, req.pct);
  const failed = !met && !consentMet(total - refused, total, req.comparator, req.pct);
  return { given, refused, total, met, failed, waiting: met || failed ? [] : waiting };
}

export const consentLabel = (req: Pick<ConsentRequirement, "comparator" | "pct" | "label">) => `${CONSENT_COMPARATOR_LABEL[req.comparator]} ${req.pct}% of ${req.label}`;

/** Where a proposal goes once its approvals are in: taken, or waiting for consent. */
export function statusOnceApproved(consent: ConsentRequirement | null, records: ConsentRecord[]): DecisionStatus {
  if (!consent) return "taken";
  const p = consentProgress(consent, records);
  return p.met ? "taken" : p.failed ? "not_taken" : "awaiting_consent";
}

/** Who still has to approve a proposal (none once it's past approval). */
export function waitingOn(d: Pick<Decision, "status" | "approval" | "approvals">): string[] {
  if (d.status !== "proposed" || !d.approval) return [];
  return approvalProgress(d.approval, d.approvals).waiting;
}

/* ------------------------------------------------------------------ */
/* Dates                                                               */
/* ------------------------------------------------------------------ */

/** Taken, and past the date after which it can't be revisited. */
export const isFinal = (d: Pick<Decision, "finalAfter">, today: ISODate) => !!d.finalAfter && today > d.finalAfter;

export type DecisionFlag = "review_due" | "review_soon" | "final_soon" | "final";
export const DECISION_FLAG_LABEL: Record<DecisionFlag, string> = { review_due: "Review due", review_soon: "Review soon", final_soon: "Final soon", final: "Final" };

/**
 * What a decision's dates say today. A taken decision comes up for review on
 * its review date; any live decision warns as the date it can't be revisited
 * after comes close, and a taken one is final once it's passed.
 */
export function decisionFlags(d: Pick<Decision, "status" | "reviewBy" | "finalAfter">, today: ISODate, soonDays: number): DecisionFlag[] {
  const out: DecisionFlag[] = [];
  const soon = addDays(today, soonDays);
  if (d.status === "taken" && d.reviewBy) {
    if (d.reviewBy <= today) out.push("review_due");
    else if (d.reviewBy <= soon) out.push("review_soon");
  }
  if (d.finalAfter && (d.status === "taken" || d.status === "proposed" || d.status === "awaiting_consent")) {
    if (today > d.finalAfter) {
      if (d.status === "taken") out.push("final");
    } else if (d.finalAfter <= soon) out.push("final_soon");
  }
  return out;
}
